import type { SoapRequestEnvelope, SoapResponseEnvelope } from '@zimbra-webif/types';
import { TransportError } from './errors';

/** Injected fetch implementation (browser global, BFF's node fetch, or a mock). */
export type FetchLike = typeof fetch;

export interface TransportOptions {
  /**
   * Origin or path prefix the SOAP endpoint lives under. The transport POSTs to
   * `${baseUrl}/service/soap`. In the browser this is typically the BFF path
   * (e.g. "" same-origin, or "/api"); on the BFF it is the Zimbra origin.
   */
  baseUrl: string;
  /** Defaults to the global `fetch`. Provide one explicitly in non-DOM runtimes. */
  fetch?: FetchLike;
  /** Extra headers merged into every request (e.g. CSRF token). */
  headers?: Record<string, string>;
}

/** Thin JSON-SOAP transport. Knows how to POST an envelope and parse the reply. */
export class SoapTransport {
  private readonly url: string;
  private readonly fetchImpl: FetchLike;
  private readonly headers: Record<string, string>;

  constructor(opts: TransportOptions) {
    const base = opts.baseUrl.replace(/\/+$/, '');
    this.url = `${base}/service/soap`;
    const f = opts.fetch ?? (typeof fetch !== 'undefined' ? fetch : undefined);
    if (!f) {
      throw new TransportError('No fetch implementation available; pass opts.fetch');
    }
    // Bind to preserve `this` for the DOM global.
    this.fetchImpl = f.bind(globalThis) as FetchLike;
    this.headers = opts.headers ?? {};
  }

  async post(envelope: SoapRequestEnvelope): Promise<SoapResponseEnvelope> {
    let res: Response;
    try {
      res = await this.fetchImpl(this.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.headers },
        body: JSON.stringify(envelope),
      });
    } catch (cause) {
      throw new TransportError('SOAP request failed (network)', undefined, { cause });
    }

    // Zimbra returns SOAP Faults with HTTP 500 but a valid JSON body; the client
    // layer inspects Body.Fault. Any other non-2xx is a real transport error.
    if (!res.ok && res.status !== 500) {
      throw new TransportError(`SOAP HTTP ${res.status}`, res.status);
    }

    try {
      return (await res.json()) as SoapResponseEnvelope;
    } catch (cause) {
      throw new TransportError('SOAP response was not valid JSON', res.status, { cause });
    }
  }
}
